import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { updateBranch } from "../api/branchesApi";

const INITIAL_FORM_DATA = {
  branchName: "",
  addressLine: "",
  city: "",
  state: "",
  country: "US",
  zipCode: "",
};

function validateBranchFields(formData) {
  const errors = {};

  if (!formData.branchName) {
    errors.branchName = "Branch name is required";
  } else if (formData.branchName.length > 100) {
    errors.branchName = "Branch name must not exceed 100 characters";
  }

  if (!formData.addressLine) {
    errors.addressLine = "Address line is required";
  } else if (formData.addressLine.length > 50) {
    errors.addressLine = "Address line must not exceed 50 characters";
  }

  if (!formData.city) {
    errors.city = "City is required";
  }

  if (!formData.state) {
    errors.state = "State is required";
  }

  if (!formData.zipCode) {
    errors.zipCode = "Zip code is required";
  } else if (!/^\d{5}$/.test(formData.zipCode)) {
    errors.zipCode = "Zip code must be exactly 5 digits";
  }

  return errors;
}

export default function useEditBranch({ accessToken, userId, onSuccess }) {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [editingBranchId, setEditingBranchId] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = useCallback((branch) => {
    const addr = branch.address || {};
    setEditingBranchId(branch.id);
    setFormData({
      branchName: branch.branch_name || "",
      addressLine: addr.address_line || "",
      city: addr.city || "",
      state: addr.state || "",
      country: addr.country || "US",
      zipCode: addr.zip_code || "",
    });
    setErrors({});
    setSubmitError("");
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setFormData(INITIAL_FORM_DATA);
    setEditingBranchId(null);
    setErrors({});
    setSubmitError("");
  }, []);

  const handleChange = useCallback((e) => {
    const { id, name, value } = e.target;
    const key = name || id;
    if (!key) return;
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitError("");

      const validationErrors = validateBranchFields(formData);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsSubmitting(true);
      try {
        await updateBranch({ accessToken, userId, branchId: editingBranchId, formData });
        toast.success("Branch updated successfully");
        closeModal();
        onSuccess?.();
      } catch (err) {
        const msg = err.message || "Failed to update branch. Please try again.";
        setSubmitError(msg);
        toast.error(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, accessToken, userId, editingBranchId, closeModal, onSuccess],
  );

  return {
    showModal,
    openModal,
    closeModal,
    formData,
    errors,
    submitError,
    isSubmitting,
    handleChange,
    handleSubmit,
  };
}
